/**
 * @fileoverview Templates management page with file-based storage
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Plus, RefreshCw, AlertCircle } from 'lucide-react';
import { TemplateCard } from '@/components/templates/TemplateCard';
import { TemplateUploadModal } from '@/components/templates/TemplateUploadModal';

export const TemplatesPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('templates.error_load', 'Error al cargar plantillas'));
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setError(null);
    try {
      const response = await fetch('/api/templates/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('templates.error_sync', 'Error al sincronizar plantillas'));
      }

      const data = await response.json();
      const { results } = data;
      alert(`${t('templates.sync_complete', 'Sincronización completada')}:\n- ${t('templates.scanned', 'Archivos escaneados')}: ${results.scanned}\n- ${t('templates.added', 'Nuevos')}: ${results.added}\n- ${t('templates.updated', 'Actualizados')}: ${results.updated}`);

      await loadTemplates();
    } catch (err) {
      console.error('Error syncing templates:', err);
      setError(err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleResetSync = async () => {
    if (!confirm(t('templates.confirm_reset_sync', '¿Estás seguro? Esto borrará todos los templates de la base de datos y los volverá a cargar desde el filesystem.'))) {
      return;
    }

    setIsSyncing(true);
    setError(null);
    try {
      const response = await fetch('/api/templates/reset-sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('templates.error_reset_sync', 'Error al resetear y sincronizar'));
      }

      const data = await response.json();
      alert(t('templates.reset_sync_success', 'Base de datos reseteada y templates cargados desde el filesystem con éxito.'));
      await loadTemplates();
    } catch (err) {
      console.error('Error in reset-sync:', err);
      setError(err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm(t('templates.confirm_delete_all', '¿Estás seguro de borrar TODOS los templates de la base de datos? (Los archivos en el filesystem NO se borrarán)'))) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/templates/all', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('templates.error_delete_all', 'Error al borrar todos los templates'));
      }

      alert(t('templates.delete_all_success', 'Todos los templates han sido eliminados de la base de datos.'));
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting all templates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    loadTemplates();
  };

  const handleEdit = (template) => {
    // TODO: Implement edit functionality
    console.log('Edit template:', template);
  };

  const handleClone = async (template) => {
    // TODO: Implement clone functionality
    console.log('Clone template:', template);
  };

  const handleDelete = async (template) => {
    if (!confirm(t('templates.confirm_delete', '¿Estás seguro de eliminar la plantilla "{{name}}"?', { name: template.name }))) {
      return;
    }

    try {
      const response = await fetch(`/api/templates/${template.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(t('templates.error_delete', 'Error al eliminar plantilla'));
      }

      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert(err.message);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#192A42] tracking-tight">
            {t('templates.title', 'Plantillas de Documentos')}
          </h1>
          <p className="mt-2 text-slate-500 text-lg">
            {t('templates.subtitle', 'Gestiona tus plantillas dinámicas para emitir certificados')}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Sync Dropdown / Group */}
          <div className="flex bg-white rounded-lg border-2 border-slate-300 overflow-hidden">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              title={t('templates.sync_tooltip', 'Sincronizar cambios nuevos del filesystem')}
              className="px-4 py-2.5 text-slate-700 font-bold hover:bg-blue-50 hover:text-blue-600 border-r border-slate-300 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? t('templates.syncing', 'Sinc...') : t('templates.sync', 'Sincronizar')}
            </button>
            <button
              onClick={handleResetSync}
              disabled={isSyncing}
              title={t('templates.reset_sync_tooltip', 'Borrar base y recargar todo desde filesystem')}
              className="px-4 py-2.5 text-slate-700 font-bold hover:bg-orange-50 hover:text-orange-600 border-r border-slate-300 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              {t('templates.reset_sync', 'Reset & Sync')}
            </button>
            <button
              onClick={handleDeleteAll}
              disabled={isLoading || isSyncing}
              title={t('templates.delete_all_tooltip', 'Borrar todos los templates de la base de datos')}
              className="px-3 py-2.5 text-slate-700 font-bold hover:bg-red-50 hover:text-red-600 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <AlertCircle className="w-4 h-4" />
              {t('templates.clear_db', 'Limpiar DB')}
            </button>
          </div>

          {/* New Template Button */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-6 py-2.5 rounded-lg bg-[#2563EB] text-white font-bold hover:bg-[#1d4ed8] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('templates.new_template', 'Nueva Plantilla')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-500">{t('templates.loading', 'Cargando plantillas...')}</p>
          </div>
        </div>
      ) : templates.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6">
            <Plus className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">{t('templates.empty_title', 'No hay plantillas')}</h3>
          <p className="text-slate-500 text-center max-w-md mb-6">
            {t('templates.empty_desc', 'Crea tu primera plantilla para comenzar a emitir certificados personalizados')}
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-6 py-3 rounded-lg bg-[#2563EB] text-white font-bold hover:bg-[#1d4ed8] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('templates.create_first', 'Crear Primera Plantilla')}
          </button>
        </div>
      ) : (
        /* Templates Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onClone={handleClone}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <TemplateUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};
